import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Video, Flag, Play, Pause, ChevronLeft, ChevronRight, Image as ImageIcon, Mic } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function AdminAttempts() {
  const [items, setItems] = useState([]);
  const [proctor, setProctor] = useState(null); // { attempt, frames, audios, currentIdx, playing }
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fps, setFps] = useState(2); // frames per second
  const playerRef = useRef(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/exam-attempts");
    setItems(r.data || []);
  };
  const publish = async (id) => {
    try {
      await api.put(`/admin/exam-attempts/${id}/publish-result`);
      toast.success("Result published");
      load();
    } catch { toast.error("Failed"); }
  };
  const viewProctor = async (attempt) => {
    try {
      const [framesRes, audiosRes] = await Promise.all([
        api.get(`/admin/exam-attempts/${attempt.id}/snapshots`),
        api.get(`/admin/exam-attempts/${attempt.id}/audio`),
      ]);
      setProctor({ attempt, frames: framesRes.data || [], audios: audiosRes.data || [] });
      setIdx(0);
      setPlaying(true);
    } catch { toast.error("Failed to load proctor recordings"); }
  };
  const flagSuspicious = async () => {
    const reason = window.prompt("Flag this attempt as suspicious? Enter reason:");
    if (!reason) return;
    try {
      await api.post(`/admin/exam-attempts/${proctor.attempt.id}/proctor-flag?reason=${encodeURIComponent(reason)}`);
      toast.success("Flagged in cheating logs");
    } catch { toast.error("Failed"); }
  };

  // Stop-motion playback
  useEffect(() => {
    if (!proctor || !playing || proctor.frames.length === 0) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % proctor.frames.length);
    }, 1000 / fps);
    return () => clearInterval(id);
  }, [proctor, playing, fps]);

  const currentFrame = proctor?.frames?.[idx];

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold">Exam Attempts</h1>
      <p className="text-sm text-slate-500 mt-1">Review each attempt's proctor recording (video frames + audio), flag, and declare results.</p>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Student</th>
              <th className="p-3 font-semibold">Test</th>
              <th className="p-3 font-semibold">Score</th>
              <th className="p-3 font-semibold">Violations</th>
              <th className="p-3 font-semibold">Submitted</th>
              <th className="p-3 font-semibold">Result</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`attempt-${a.id}`}>
                <td className="p-3 font-semibold">{a.user_name}</td>
                <td className="p-3">{a.test_title}</td>
                <td className="p-3"><span className="font-mono font-bold">{a.score} / {a.total}</span></td>
                <td className="p-3"><span className={`text-xs font-bold ${a.violations_count > 0 ? "text-red-600" : "text-emerald-600"}`}>{a.violations_count || 0}</span></td>
                <td className="p-3 text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}</td>
                <td className="p-3">
                  {a.is_result_published ? (
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-emerald-100 text-emerald-700">Declared</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-amber-100 text-amber-700">Hidden</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => viewProctor(a)} className="text-xs font-semibold text-brand hover:bg-blue-50 rounded px-2 py-1.5 inline-flex items-center gap-1" data-testid={`proctor-${a.id}`}>
                      <Video size={12} /> Proctor
                    </button>
                    {!a.is_result_published && (
                      <button onClick={() => publish(a.id)} className="text-xs font-semibold bg-brand text-white rounded px-3 py-1.5 inline-flex items-center gap-1" data-testid={`publish-${a.id}`}>
                        <CheckCircle2 size={12} /> Declare
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500">No attempts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!proctor} onOpenChange={(o) => !o && setProctor(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proctor Recording — {proctor?.attempt?.user_name}</DialogTitle>
            <DialogDescription>
              <span className="inline-flex items-center gap-1 mr-4"><ImageIcon size={12} /> {proctor?.frames?.length || 0} video frames</span>
              <span className="inline-flex items-center gap-1"><Mic size={12} /> {proctor?.audios?.length || 0} audio clips</span>
            </DialogDescription>
          </DialogHeader>

          {(!proctor?.frames?.length && !proctor?.audios?.length) ? (
            <div className="p-10 text-center text-slate-500">
              <Video size={36} className="mx-auto text-slate-300" />
              <div className="mt-3">No proctor recordings for this attempt.</div>
            </div>
          ) : (
            <>
              {/* Stop-motion video frames */}
              {proctor?.frames?.length > 0 && (
                <>
                  <div className="rounded-xl overflow-hidden bg-black aspect-video relative">
                    <img
                      src={ABS(currentFrame?.url || "")}
                      alt={`Frame ${idx + 1}`}
                      className="w-full h-full object-contain"
                      data-testid="proctor-frame"
                    />
                    <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-semibold px-3 py-1 rounded">
                      Frame {idx + 1} / {proctor.frames.length}
                    </div>
                    <div className="absolute top-3 right-3 bg-black/70 text-white text-xs font-semibold px-3 py-1 rounded">
                      {currentFrame?.timestamp && new Date(currentFrame.timestamp).toLocaleTimeString()}
                    </div>
                  </div>

                  {/* Transport */}
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => setPlaying(!playing)}
                      className="px-4 py-2 rounded-lg bg-brand text-white font-semibold text-sm inline-flex items-center gap-1.5"
                      data-testid="proctor-play-toggle"
                    >
                      {playing ? <Pause size={14} /> : <Play size={14} />} {playing ? "Pause" : "Play"}
                    </button>
                    <button
                      onClick={() => setIdx(Math.max(0, idx - 1))}
                      className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm inline-flex items-center gap-1"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <button
                      onClick={() => setIdx(Math.min(proctor.frames.length - 1, idx + 1))}
                      className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm inline-flex items-center gap-1"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                    <div className="flex items-center gap-2 ml-auto text-xs text-slate-600">
                      <span>Speed:</span>
                      {[1, 2, 4, 8].map((f) => (
                        <button
                          key={f}
                          onClick={() => setFps(f)}
                          className={`px-2 py-1 rounded text-xs font-bold ${f === fps ? "bg-brand text-white" : "bg-slate-100 hover:bg-slate-200"}`}
                        >
                          {f}×
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Timeline slider */}
                  <input
                    type="range"
                    min={0}
                    max={proctor.frames.length - 1}
                    value={idx}
                    onChange={(e) => setIdx(Number(e.target.value))}
                    className="mt-3 w-full"
                    data-testid="proctor-timeline"
                  />
                </>
              )}

              {/* Audio clips list */}
              {proctor?.audios?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-slate-600 mb-3">Audio Recordings</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {proctor.audios.map((a, i) => (
                      <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-200">
                        <Mic size={14} className="text-brand shrink-0" />
                        <div className="text-xs text-slate-600 shrink-0 w-20 font-mono">
                          {new Date(a.timestamp).toLocaleTimeString()}
                        </div>
                        <audio src={ABS(a.url)} controls className="flex-1 h-8" data-testid={`audio-clip-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-4 flex items-center justify-between pt-4 border-t">
            <div className="text-xs text-slate-500">
              Score: <b className="text-slate-900">{proctor?.attempt?.score}/{proctor?.attempt?.total}</b> · Violations: <b className="text-red-600">{proctor?.attempt?.violations_count || 0}</b>
            </div>
            <button onClick={flagSuspicious} className="bg-red-50 text-red-600 font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1 hover:bg-red-100" data-testid="flag-suspicious">
              <Flag size={14} /> Flag as Suspicious
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
